/*
 © Copyright 2026 Daikin Industries, Ltd. All rights reserved.

 This software is the proprietary information of Daikin Industries, Ltd.
 By using this software, you agree to the following terms:

 1. You are granted a revocable non-exclusive, non-transferable license to use this software.
 2. You agree not to modify, distribute, or create derivative works based on this software.
 3. Unauthorized copying, distribution, or use of any portion of this software is strictly prohibited.
 4. This software is protected by copyright laws and international treaties.
 5. Any violation of these terms will result in the termination of your license and may result in legal action.
 6. This is Daikin's proprietary software and may not be used in an unauthorized manner or without express authorization from Daikin.
*/

import { ApplicationConfig, provideZoneChangeDetection, APP_INITIALIZER } from '@angular/core';
import { provideRouter } from '@angular/router';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import {
  MSAL_INSTANCE,
  MSAL_GUARD_CONFIG,
  MSAL_INTERCEPTOR_CONFIG,
  MsalService,
  MsalGuard,
  MsalBroadcastService,
  MsalInterceptor
} from '@azure/msal-angular';
import {
  IPublicClientApplication,
  PublicClientApplication,
  InteractionType
} from '@azure/msal-browser';
import { HTTP_INTERCEPTORS } from '@angular/common/http';

import { routes } from './app.routes';
import { getMsalConfig } from './auth-config';
import { RedirectService } from './shared/services/auth/redirect.service';
import { ScrollService } from './shared/utils/scroll.service';
import { VersionService } from './shared/utils/version.service';
import { ConfigService } from './shared/services/config.service';

let msalInstance: IPublicClientApplication | null = null;

export function MSALInstanceFactory(): IPublicClientApplication {
  if (!msalInstance) {
    throw new Error('MSAL instance not initialized. Ensure APP_INITIALIZER has run.');
  }
  return msalInstance;
}

export function MSALGuardConfigFactory(configService: ConfigService) {
  const config = configService.getConfig();
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: config.loginConfig.scopes
    },
    loginFailedRoute: '/'
  };
}

export function MSALInterceptorConfigFactory() {
  return {
    interactionType: InteractionType.Redirect,
    // Commented out: protectedResourceMap not used - no HTTP requests to protected resources
    protectedResourceMap: new Map() // environment.protectedResourceMap
  };
}

export function initializeApp(configService: ConfigService): () => Promise<void> {
  return async () => {
    try {
      // Step 1: Load configuration
      await configService.loadConfig();
      console.log('Configuration loaded successfully');

      // Step 2: Create and initialize MSAL instance
      const msalConfig = getMsalConfig(configService);
      msalInstance = new PublicClientApplication(msalConfig);
      await msalInstance.initialize();
      console.log('MSAL initialized successfully');
    } catch (error) {
      console.error('Application initialization failed:', error);
      throw error;
    }
  };
}

export const appConfig: ApplicationConfig = {
  providers: [
    provideZoneChangeDetection({ eventCoalescing: true }),
    provideRouter(routes),
    provideHttpClient(withInterceptorsFromDi()),
    ConfigService,
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [ConfigService],
      multi: true
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
      deps: [ConfigService]
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true
    },
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    RedirectService,
    ScrollService,
    VersionService
  ]
};
